import { useEffect, useRef, useState } from "react";

// Toolkit imports
import {
  fetchExerciseDetails,
  fetchExerciseVariant,
  fetchLessonAttempt,
  fetchLessonResources,
  getQuiz,
  LessonAttemptResponse,
} from "sj-ui-toolkit/src/api";
import { Assessment, Question, NextLesson } from "sj-ui-toolkit/src/api/types/quizTypes";
import { Variant, Experiment } from "sj-ui-toolkit/src/api/types/experimentTypes";
import { Resource } from "sj-ui-toolkit/src/api/types/lessonTypes";
import { getContentClient, getExperimentClient } from "sj-ui-toolkit/src/client";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { convertLessonDetails } from "../helpers/lessons";

const difficultyMapping: Record<string, string> = {
  "Break/Fix": "Basic",
  Coding_Challenge: "Intermediate",
  Ctf_Challenge: "Advanced",
};

/** Tournament lesson context consumed by the loader (a subset of the tournament content). */
interface TournamentLessonContent {
  assessmentProps?: {
    assessment?: Assessment;
    question?: Question | null;
    questionsData?: Question[];
  };
  experimentPassed?: boolean | null;
  lessonPassed?: boolean | null;
}

/**
 * A single exercise/experiment payload. Its shape varies by source (tournament experiment
 * vs. the Rails exercise API), so the fields the loader touches are kept loose here.
 */
export interface LessonExercise {
  id?: number;
  name?: string;
  experiment?: { id: number };
  variant?: Variant;
  [key: string]: unknown;
}

/**
 * Exercise payload passed in for tournaments or exercise-only lessons.
 */
export interface ExerciseContentInput {
  experiment?: LessonExercise;
  steps?: number[];
}

interface UseLessonLoaderArgs {
  lessonKey?: string | null;
  tournamentContent?: TournamentLessonContent | null;
  exerciseContent?: ExerciseContentInput | null;
  roleId?: string;
}

/**
 * The assembled lesson-details object surfaced to lesson/exercise/tournament views. It is
 * built up incrementally from several sources, so most fields are optional;
 * SHRPA-4120 will tighten this further once the loader returns a discriminated union.
 */
export interface LessonDetails {
  id?: number;
  type?: string;
  title?: string;
  description?: string;
  level?: string;
  difficulty?: string;
  duration?: string | number;
  videoId?: string | null;
  isBreakFix?: boolean;
  hasExercise?: boolean;
  since?: string;
  events?: {
    isConsumed?: boolean;
    watchedVideo?: boolean;
    readTranscript?: boolean;
  };
  assessment?: Assessment;
  question?: Question | null;
  questionsData?: Question[];
  showBeforeSurvey?: boolean | null;
  showAfterSurvey?: boolean;
  nextLesson?: NextLesson;
  experimentPassed?: boolean | null;
  lessonPassed?: boolean | null;
  resources?: Resource[];
  experiment?: LessonExercise | Experiment;
  variant?: Variant;
  steps?: number[];
  lessonType?: string;
  completionType?: string;
  lessonKey?: string;
  contentKey?: string;
}

/**
 * Builds the common lesson details object from Rails data, source-specific overrides,
 * tournament content, and assessment data.
 */
async function buildLessonDetails(
  data: LessonAttemptResponse & { difficulty?: string },
  overrides: Partial<LessonDetails>,
  tournamentContent: TournamentLessonContent | null,
  roleId?: string
): Promise<LessonDetails> {
  const details: LessonDetails = {
    id: data.lesson.id,
    type: data.type,
    title: data.lesson.name,
    description: data.lesson.description,
    level: data.levelName,
    difficulty: data.lesson.isBreakFixLesson ? difficultyMapping[data.type ?? ""] : data.difficulty,
    duration: data.lesson.duration,
    videoId: data.lesson.videoId,
    isBreakFix: data.lesson.isBreakFixLesson,
    hasExercise: data.lesson.hasExperiment,
    since: data.lesson.since,
    events: {
      isConsumed: data.isConsumed,
      watchedVideo: data.watchedVideo,
      readTranscript: data.readTranscript,
    },
    ...overrides,
  };

  if (tournamentContent?.assessmentProps?.assessment) {
    details.assessment = tournamentContent.assessmentProps.assessment;
    details.question = tournamentContent.assessmentProps.question;
    details.questionsData = tournamentContent.assessmentProps.questionsData;
    details.showBeforeSurvey = false;
  } else {
    const quiz = await getQuiz(data.lesson.lessonKey, roleId);
    details.assessment = quiz.assessment;
    details.questionsData = quiz.questionsData;
    details.showBeforeSurvey = quiz.assessment.showBeforeSurvey;
    details.nextLesson = quiz.nextLesson;
  }

  details.experimentPassed = tournamentContent?.experimentPassed;
  details.lessonPassed = tournamentContent?.lessonPassed;

  return details;
}

/**
 * useLessonLoader Hook
 *
 * Custom hook to fetch and manage lesson details from multiple sources.
 * Combines data from Rails API and HackEdu content API for break/fix lessons.
 *
 * @param args - The lesson key plus optional tournament, exercise, and path-role context
 */
export function useLessonLoader({
  lessonKey,
  tournamentContent = null,
  exerciseContent = null,
  roleId,
}: UseLessonLoaderArgs) {
  const contentClient = getContentClient();
  const experimentClient = getExperimentClient();
  const [lessonDetails, setLessonDetails] = useState<LessonDetails | null>(null);
  const exerciseReservationRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { selectedTenant } = useUserContext();

  // If we have an exercise, make the reservation now and set an interval to re-reserve every 10 minutes
  useEffect(() => {
    if (lessonDetails?.hasExercise && lessonDetails?.variant) {
      const variant = lessonDetails.variant;
      experimentClient.prepareTest(
        variant.lessonKey,
        variant.lessonVersion,
        variant.currentVariant
      );

      exerciseReservationRef.current = setInterval(
        () => {
          experimentClient.prepareTest(
            variant.lessonKey,
            variant.lessonVersion,
            variant.currentVariant
          );
        },
        1000 * 10 * 60
      );

      return () => {
        if (exerciseReservationRef.current) clearInterval(exerciseReservationRef.current);
      };
    }
  }, [lessonDetails?.hasExercise, lessonDetails?.variant]);

  // Load lesson details on mount or when lessonKey changes
  useEffect(() => {
    setLessonDetails(null);

    async function doLoad() {
      if (!lessonKey && !exerciseContent) {
        return;
      }

      let combinedLessonDetails: LessonDetails = {};
      let svcContentDetails: Partial<LessonDetails> = {};

      if (lessonKey) {
        try {
          const railsDetails = await fetchLessonAttempt(lessonKey);
          const response = await contentClient.getLessonPublic(lessonKey, selectedTenant);
          svcContentDetails = convertLessonDetails(response.data!);
          combinedLessonDetails = await buildLessonDetails(
            railsDetails,
            svcContentDetails,
            tournamentContent,
            roleId
          );
        } catch (error) {
          console.error("Error fetching lesson details:", error);
          return;
        }

        // Use exercise details from exerciseContent (i.e. tournament data) if available
        // otherwise fetch from Rails API
        if (combinedLessonDetails.hasExercise) {
          // exerciseContent (tournament) and fetchExerciseDetails() return differently shaped
          // exercise payloads that the code below reads uniformly; unify loosely here — T13 reconciles.
          const exerciseDetails = (exerciseContent?.experiment ??
            (await fetchExerciseDetails(String(combinedLessonDetails.id)))) as LessonExercise;
          const variantDetails = await fetchExerciseVariant(exerciseDetails.experiment!.id);
          exerciseDetails.variant = variantDetails;
          combinedLessonDetails = { ...combinedLessonDetails, ...exerciseDetails };
        }

        // Load resources
        try {
          const resources = await fetchLessonResources(combinedLessonDetails.id!);
          if (resources?.length > 0) {
            combinedLessonDetails.resources = resources;
          }
        } catch (error) {
          console.error("Error fetching lesson resources:", error);
          return;
        }
      } else if (exerciseContent !== null) {
        // We're in an exercise-only context
        const { experiment, steps } = exerciseContent;
        const title = experiment?.name;
        const variant = await fetchExerciseVariant(experiment!.id!);
        const staticDetails = {
          type: "Exercise",
          level: "Advanced",
          duration: 10,
          hasExercise: true,
        };
        combinedLessonDetails = { title, experiment, variant, steps, ...staticDetails };
      }

      // Update state with combined details
      setLessonDetails(combinedLessonDetails);
    }

    doLoad();
  }, [lessonKey, tournamentContent, exerciseContent, roleId, selectedTenant]);

  return {
    lessonDetails,
    setLessonDetails,
  };
}
